import React, { useEffect, useState } from "react";
import {
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";

export const EnhancedErrorHandler = ({ error, duration = 10000 }) => {
  const [open, setOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    if (error?.message) {
      setOpen(true);

      const timer = setTimeout(() => {
        setOpen(false);
      }, duration);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [error, duration]);

  // If error is a simple string or basic error object, convert to enhanced format
  const normalizedError = React.useMemo(() => {
    if (!error) return null;

    if (typeof error === "string") {
      return {
        message: error,
        timestamp: new Date().toISOString(),
        code: "unknown",
      };
    }

    // If it's already our enhanced error format
    if (error.timestamp && error.code) {
      return error;
    }

    // If it's a basic error object
    return {
      message: error.message || "An unknown error occurred",
      code: error.code || "unknown",
      timestamp: new Date().toISOString(),
      status: error.status,
      details: error.details || null,
      requestParams: error.requestParams || null,
    };
  }, [error]);

  // Check if this is a timeout error
  const isTimeoutError = React.useMemo(() => {
    if (!normalizedError) return false;

    return (
      normalizedError.code === "connection_error" &&
      normalizedError.message?.includes("timeout")
    ) || normalizedError.message?.toLowerCase().includes("timed out");
  }, [normalizedError]);

  // Generate user-friendly message for timeout errors
  const getErrorMessage = React.useMemo(() => {
    if (!normalizedError) return "";

    if (isTimeoutError) {
      return "Request timed out - try using more specific filters to reduce the data load";
    }

    return normalizedError.message;
  }, [normalizedError, isTimeoutError]);

  // Generate helpful suggestions for timeout errors
  const getFilterSuggestions = React.useMemo(() => {
    if (!isTimeoutError || !normalizedError?.requestParams) return null;

    const suggestions = [];
    const params = normalizedError.requestParams;

    if (!params.hours_to_event || parseInt(params.hours_to_event) > 24) {
      suggestions.push("Set 'Hours to Event' to 24 hours or less");
    }
    if (!params.sport || params.sport.includes(",")) {
      suggestions.push("Select a specific sport instead of multiple sports");
    }
    if (!params.max_odds) {
      suggestions.push("Set a 'Max Odds' limit (e.g., 5 or 10)");
    }
    if (!params.bookie_count) {
      suggestions.push("Set a minimum 'Bookie Count' (e.g., 3 or more)");
    }

    return suggestions.length > 0 ? suggestions : null;
  }, [isTimeoutError, normalizedError]);

  const handleCopyError = () => {
    const errorText = JSON.stringify(normalizedError, null, 2);
    navigator.clipboard.writeText(errorText);
  };

  const hasDetails =
    normalizedError &&
    (normalizedError.code !== "unknown" ||
      normalizedError.details ||
      normalizedError.requestParams ||
      normalizedError.status);

  const formatErrorDetails = () => {
    return (
      <Box sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Error Code: {normalizedError.code}
        </Typography>
        {normalizedError.status && (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Status: {normalizedError.status}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Time: {normalizedError.timestamp}
        </Typography>
        {normalizedError.details && (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Details: {JSON.stringify(normalizedError.details, null, 2)}
          </Typography>
        )}
        {normalizedError.requestParams && (
          <Typography variant="body2" color="text.secondary">
            Request Parameters:{" "}
            {JSON.stringify(normalizedError.requestParams, null, 2)}
          </Typography>
        )}
      </Box>
    );
  };

  if (!normalizedError) return null;

  return (
    <>
      <Snackbar
        open={open}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={isTimeoutError ? "warning" : "error"}
          onClose={() => setOpen(false)}
          action={
            hasDetails ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => setDetailsOpen(true)}
              >
                View Details
              </Button>
            ) : undefined
          }
          sx={isTimeoutError ? { maxWidth: 600 } : undefined}
        >
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {isTimeoutError ? "Request Timed Out" : "Error"}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {getErrorMessage}
            </Typography>
            {getFilterSuggestions && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" fontWeight="medium">
                  Try these suggestions:
                </Typography>
                <ul style={{ margin: "4px 0 0 0", paddingLeft: "20px" }}>
                  {getFilterSuggestions.map((suggestion, index) => (
                    <li key={index}>
                      <Typography variant="caption">{suggestion}</Typography>
                    </li>
                  ))}
                </ul>
              </Box>
            )}
          </Box>
        </Alert>
      </Snackbar>

      {hasDetails && (
        <Dialog
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Error Details
            <IconButton
              aria-label="close"
              onClick={() => setDetailsOpen(false)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>{formatErrorDetails()}</DialogContent>
          <DialogActions>
            <Button startIcon={<ContentCopyIcon />} onClick={handleCopyError}>
              Copy Error Details
            </Button>
            <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
